export type RuntimeAuthConfig = {
  domain: string;
  webClientId: string;
  redirectUri: string;
  logoutUri: string;
  scopes: string[];
  issuerUrl?: string;
};

export type RuntimeAccountServiceConfig = {
  apiBaseUrl: string;
};

export type RuntimeHealthRecordServiceConfig = {
  apiBaseUrl: string;
};

export type RuntimeMedicationServiceConfig = {
  apiBaseUrl: string;
};

export type RuntimeDocumentServiceConfig = {
  apiBaseUrl: string;
};

export type RuntimeNotificationServiceConfig = {
  apiBaseUrl: string;
  // Absent until a VAPID keypair has been provisioned for this environment -- browser push
  // subscription must treat this as "not configured yet", not as an error.
  vapidPublicKey: string | null;
};

export type RuntimeMedicineOrderServiceConfig = {
  apiBaseUrl: string;
};

export type RuntimeConfig = {
  environment?: string;
  region?: string;
  webUrl?: string;
  accountService: RuntimeAccountServiceConfig | null;
  healthRecordService: RuntimeHealthRecordServiceConfig | null;
  medicationService: RuntimeMedicationServiceConfig | null;
  documentService: RuntimeDocumentServiceConfig | null;
  notificationService: RuntimeNotificationServiceConfig | null;
  // Absent until medicine-order-service has been deployed to this environment -- the Order
  // Medicines area must treat this as "not available here yet", not as an error, exactly like
  // notificationService before a VAPID key is provisioned.
  medicineOrderService: RuntimeMedicineOrderServiceConfig | null;
  auth: RuntimeAuthConfig | null;
};

type RawRuntimeConfig = {
  environment?: string;
  region?: string;
  webUrl?: string;
  accountService?: {
    apiBaseUrl?: string;
  } | null;
  healthRecordService?: {
    apiBaseUrl?: string;
  } | null;
  medicationService?: {
    apiBaseUrl?: string;
  } | null;
  documentService?: {
    apiBaseUrl?: string;
  } | null;
  notificationService?: {
    apiBaseUrl?: string;
    vapidPublicKey?: string | null;
  } | null;
  medicineOrderService?: {
    apiBaseUrl?: string;
  } | null;
  auth?: {
    domain?: string;
    webClientId?: string;
    redirectUri?: string;
    logoutUri?: string;
    scopes?: string[];
    issuerUrl?: string;
  } | null;
};

function normalizeUrl(value: string) {
  return value.replace(/\/+$/, "");
}

// The established mechanism for DEV-only frontend features: deploy-frontend.yml writes this
// exact value ("dev" | "stage" | "prod") into config.json per environment (see also
// installedAppName.ts's DeploymentEnvironment, which mirrors the same three values). Never a
// hostname/URL check -- this stays correct even if DEV's domain naming changes.
export function isDevEnvironment(environment: string | undefined): boolean {
  return environment === "dev";
}

function isCompleteAuthConfig(auth: RawRuntimeConfig["auth"]): auth is NonNullable<RawRuntimeConfig["auth"]> {
  return Boolean(
    auth?.domain &&
      auth.webClientId &&
      auth.redirectUri &&
      auth.logoutUri &&
      Array.isArray(auth.scopes) &&
      auth.scopes.length > 0,
  );
}

export function parseRuntimeConfig(config: RawRuntimeConfig | null): RuntimeConfig | null {
  if (!config) {
    return null;
  }

  let accountService: RuntimeAccountServiceConfig | null = null;
  if (config.accountService !== null && config.accountService !== undefined) {
    const apiBaseUrl = config.accountService.apiBaseUrl;
    if (!apiBaseUrl) {
      throw new Error("Runtime account-service configuration is incomplete.");
    }

    accountService = {
      apiBaseUrl: normalizeUrl(apiBaseUrl),
    };
  }

  let healthRecordService: RuntimeHealthRecordServiceConfig | null = null;
  if (config.healthRecordService !== null && config.healthRecordService !== undefined) {
    const apiBaseUrl = config.healthRecordService.apiBaseUrl;
    if (!apiBaseUrl) {
      throw new Error("Runtime health-record-service configuration is incomplete.");
    }

    healthRecordService = {
      apiBaseUrl: normalizeUrl(apiBaseUrl),
    };
  }

  let medicationService: RuntimeMedicationServiceConfig | null = null;
  if (config.medicationService !== null && config.medicationService !== undefined) {
    const apiBaseUrl = config.medicationService.apiBaseUrl;
    if (!apiBaseUrl) {
      throw new Error("Runtime medication-service configuration is incomplete.");
    }

    medicationService = {
      apiBaseUrl: normalizeUrl(apiBaseUrl),
    };
  }

  let documentService: RuntimeDocumentServiceConfig | null = null;
  if (config.documentService !== null && config.documentService !== undefined) {
    const apiBaseUrl = config.documentService.apiBaseUrl;
    if (!apiBaseUrl) {
      throw new Error("Runtime document-service configuration is incomplete.");
    }

    documentService = {
      apiBaseUrl: normalizeUrl(apiBaseUrl),
    };
  }

  let notificationService: RuntimeNotificationServiceConfig | null = null;
  if (config.notificationService !== null && config.notificationService !== undefined) {
    const apiBaseUrl = config.notificationService.apiBaseUrl;
    if (!apiBaseUrl) {
      throw new Error("Runtime notification-service configuration is incomplete.");
    }

    notificationService = {
      apiBaseUrl: normalizeUrl(apiBaseUrl),
      vapidPublicKey: config.notificationService.vapidPublicKey?.trim() || null,
    };
  }

  let medicineOrderService: RuntimeMedicineOrderServiceConfig | null = null;
  if (config.medicineOrderService !== null && config.medicineOrderService !== undefined) {
    const apiBaseUrl = config.medicineOrderService.apiBaseUrl;
    if (!apiBaseUrl) {
      throw new Error("Runtime medicine-order-service configuration is incomplete.");
    }

    medicineOrderService = {
      apiBaseUrl: normalizeUrl(apiBaseUrl),
    };
  }

  if (config.auth === null || config.auth === undefined) {
    return {
      environment: config.environment,
      region: config.region,
      webUrl: config.webUrl,
      accountService,
      healthRecordService,
      medicationService,
      documentService,
      notificationService,
      medicineOrderService,
      auth: null,
    };
  }

  if (!isCompleteAuthConfig(config.auth)) {
    throw new Error("Runtime auth configuration is incomplete.");
  }

  const auth = config.auth as Required<Pick<NonNullable<RawRuntimeConfig["auth"]>, "domain" | "webClientId" | "redirectUri" | "logoutUri" | "scopes">> &
    Pick<NonNullable<RawRuntimeConfig["auth"]>, "issuerUrl">;

  return {
    environment: config.environment,
    region: config.region,
    webUrl: config.webUrl,
    accountService,
    healthRecordService,
    medicationService,
    documentService,
    notificationService,
    medicineOrderService,
    auth: {
      domain: normalizeUrl(auth.domain),
      issuerUrl: auth.issuerUrl ? normalizeUrl(auth.issuerUrl) : undefined,
      webClientId: auth.webClientId,
      redirectUri: auth.redirectUri,
      logoutUri: auth.logoutUri,
      scopes: auth.scopes,
    },
  };
}
